import React, { useEffect } from 'react';
import { SampleRoutine, AgentEvents } from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import './routine-detail-view.scss';

interface SampleRoutineDetailViewProps {
  sampleRoutine: SampleRoutine;
  onBack: () => void;
  onSetUp: (sampleRoutine: SampleRoutine) => void;
  settingUp?: boolean;
  onRoutineCreated?: (autopilotId: string, routineName: string) => void;
}

/**
 * Parse bullet-point instructions into structured items.
 */
function parseInstructions(raw: string): {
  trigger: string;
  steps: string[];
} {
  const lines = raw
    .split('\n')
    .map((l) => l.replace(/^[*-]\s*/, '').trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return { trigger: '', steps: [] };
  }

  return {
    trigger: lines[0],
    steps: lines.slice(1),
  };
}

/**
 * Render text with **bold** markdown into React elements.
 */
function renderBoldMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

const SampleRoutineDetailView: React.FC<SampleRoutineDetailViewProps> = ({
  sampleRoutine,
  onBack,
  onSetUp,
  settingUp = false,
  onRoutineCreated,
}) => {
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  // Listen for routine creation so we can navigate to the new routine
  useEffect(() => {
    if (!userChannel || !settingUp) return;

    const handleThreadCreated = (data: any) => {
      if (!data?.thread) return;
      const thread = data.thread;
      const discussingObjects =
        thread.discussing_objects || thread.discussingObjects;
      if (!Array.isArray(discussingObjects)) return;

      const autopilotObj = discussingObjects.find(
        (obj: any) =>
          obj.discussing_object === 'Autopilot' ||
          obj.discussingObject === 'Autopilot',
      );
      if (!autopilotObj) return;

      const autopilotId =
        autopilotObj?.discussing_object_match?.autopilotId ||
        autopilotObj?.discussingObjectMatch?.autopilotId;
      if (autopilotId && onRoutineCreated) {
        onRoutineCreated(autopilotId, thread.description || sampleRoutine.name);
      }
    };

    userChannel.bind(AgentEvents.THREAD_CREATED, handleThreadCreated);
    return () => {
      userChannel.unbind(AgentEvents.THREAD_CREATED, handleThreadCreated);
    };
  }, [userChannel, settingUp, onRoutineCreated, sampleRoutine.name]);

  const parsed = parseInstructions(sampleRoutine.default_instructions);

  return (
    <div className='routine-detail-view'>
      <button
        type='button'
        className='routine-detail-view-back'
        onClick={onBack}
      >
        <ArrowLeftIcon />
        Back
      </button>

      <div className='routine-detail-view-header'>
        <div className='routine-detail-view-header-top'>
          <span className='routine-detail-view-subtitle'>SAMPLE ROUTINE</span>
          <div className='routine-detail-view-title-row'>
            <h2 className='routine-detail-view-title'>{sampleRoutine.name}</h2>
            <button
              type='button'
              className='routine-detail-view-start-btn'
              onClick={() => onSetUp(sampleRoutine)}
              disabled={settingUp}
            >
              {settingUp ? (
                <>
                  <span className='routine-detail-view-spinner' />
                  Setting up…
                </>
              ) : (
                'Set Up Routine'
              )}
            </button>
          </div>
        </div>

        {(parsed.trigger || parsed.steps.length > 0) && (
          <div className='routine-detail-view-instructions'>
            <h3 className='routine-detail-view-instructions-title'>
              What will I do?
            </h3>
            <ul className='routine-detail-view-steps'>
              {parsed.trigger && (
                <li className='routine-detail-view-step trigger'>
                  <span>{renderBoldMarkdown(parsed.trigger)}</span>
                </li>
              )}
              {parsed.steps.map((step, i) => (
                <li key={i} className='routine-detail-view-step'>
                  <span>{renderBoldMarkdown(step)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default SampleRoutineDetailView;
